import { useCallback, useRef } from "react";
import { VehicleRentalLocationFromJSON } from "@/generated/search.openapi";
import type { VehicleRentalLocation } from "@/generated/search.openapi";
import { distanceBetweenPointsInMiles, getPlaceDetails } from "@/lib/mapUtils";
import type { SupplementalSuggestionsLoader } from "../SearchInput";

export type RentalPickupArea = {
  iata: string;
  cityCode: string | null;
  query: string;
  display: string;
  type: "Airport" | "CityCenterDowntown" | "RailwayStation" | "Hotel";
};

type Options = {
  area: RentalPickupArea | null;
  onSelect: (branch: VehicleRentalLocation, area: RentalPickupArea) => void;
};

/** Optional discovery is separate from committing/resolving the pickup field. */
export function useRentalPickupSuggestions(
  options: Options
): SupplementalSuggestionsLoader {
  const current = useRef(options);
  current.current = options;
  const geometry = useRef(
    new Map<string, Promise<{ lat: number; lng: number } | null>>()
  );
  return useCallback(async ({ query, predictions, signal }) => {
    if (signal.aborted) return [];
    let area = query ? null : current.current.area;
    let point: { lat: number; lng: number };
    if (area) {
      const [lat, lng] = area.query.split(",").map(Number);
      point = { lat, lng };
    } else {
      // An airport-led query must remain an airport search, even if Google also
      // supplies a city prediction. One named anchor bounds optional lookup cost.
      if (!query || predictions[0]?.types.includes("airport")) return [];
      const anchor =
        predictions.find((row) => row.types.includes("locality")) ??
        predictions.find((row) => !row.types.includes("airport"));
      if (!anchor) return [];
      let pending = geometry.current.get(anchor.placeId);
      if (!pending) {
        pending = getPlaceDetails(anchor.placeId, ["geometry"])
          .then((result) => result?.geometry?.location?.toJSON() ?? null)
          .catch(() => null);
        geometry.current.set(anchor.placeId, pending);
      }
      const found = await pending;
      if (!found) geometry.current.delete(anchor.placeId);
      if (!found || signal.aborted) return [];
      point = found;
      area = {
        iata: "",
        cityCode: null,
        query: `${point.lat},${point.lng}`,
        display: anchor.description,
        type: anchor.types.some(
          (type) => type === "transit_station" || type === "train_station"
        )
          ? "RailwayStation"
          : "CityCenterDowntown",
      };
    }
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return [];
    const broadArea = area;
    try {
      const [directory, airports] = await Promise.all([
        fetch(
          `/api/vehicle/locations?lat=${point.lat}&lng=${point.lng}&radiusKm=10`,
          { signal }
        ),
        area.iata
          ? Promise.resolve(null)
          : fetch(`/api/vehicle/airports?lat=${point.lat}&lng=${point.lng}`, {
              signal,
            }),
      ]);
      if (signal.aborted) return [];
      if (airports?.ok) {
        const values = await airports.json();
        broadArea.iata = values[0]?.iataCode ?? "";
        broadArea.cityCode = values[0]?.iataCityCode ?? null;
      }
      if (!directory.ok || !broadArea.iata) return [];
      const wire: unknown = await directory.json();
      if (signal.aborted || !Array.isArray(wire)) return [];
      const branches = wire.map(VehicleRentalLocationFromJSON);
      return branches.map((branch) => ({
        key: `branch:${branch.metroCode}|${branch.vendorCode}|${branch.vendorLocationId}`,
        description: `${branch.vendorName}, ${branch.address}`,
        secondaryText: `${distanceBetweenPointsInMiles(point, { lat: branch.latitude, lng: branch.longitude }).toFixed(1)} miles away`,
        group: `Rental branches near ${broadArea.display}`,
        onSelect: () => {
          current.current.onSelect(branch, broadArea);
        },
      }));
    } catch {
      return [];
    }
  }, []);
}
